/**
 * Purpose: Single button language for the business design system (theme-token based).
 * Inputs: variant ('primary'|'secondary'|'danger'|'ghost'), size ('sm'|'md'|'lg'),
 *         fullWidth, loading, disabled, icon (node), href (renders an <a>),
 *         plus native button/anchor props (onClick, type, aria-*).
 * Outputs: <button> or <a> styled with `brand-*` tokens; never hardcodes palette colors.
 */
import React from 'react';

const VARIANT_CLASSES = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-600/20 disabled:hover:bg-brand-600',
  secondary:
    'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-700',
  danger:
    'bg-white text-red-700 border border-red-200 hover:bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950',
  ghost:
    'bg-transparent text-brand-700 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-gray-800',
};

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2.5 text-sm gap-2',
  lg: 'px-5 py-3 text-sm gap-2',
};

const CTAButton = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  icon = null,
  href,
  className = '',
  children,
  ...props
}) => {
  const classes = [
    'inline-flex items-center justify-center rounded-xl font-semibold transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary,
    SIZE_CLASSES[size] || SIZE_CLASSES.md,
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading ? (
        <span
          data-testid="cta-spinner"
          aria-hidden
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        icon
      )}
      {children}
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes} {...props}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={classes} disabled={disabled || loading} {...props}>
      {content}
    </button>
  );
};

export default CTAButton;
